import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Center,
  Paper,
  Title,
  Group,
  Loader,
  Stack,
  Container,
  Button,
  Text,
  Space,
} from '@mantine/core';
import { w3cwebsocket as W3CWebSocket } from 'websocket';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';
import { CircleX } from 'tabler-icons-react';
import AppContext from '../../components/Context/index';
import { sendRequest, sendRequestWithToken } from '../../services/services';
import PaymentStatus from './PaymentStatus';
import PrimaryButton from '../../components/GroupButton';

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const passId = searchParams.get('passId');
  const appContextState = useContext(AppContext);
  const { appState } = appContextState;

  const [ticketData, setTicketData] = useState();
  const [, setCardPay] = useState(false);
  const [errorPos, setErrorPos] = useState(false);
  const [response, setResponse] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');

  const siteDetails = JSON.parse(localStorage.getItem('siteDetails'));

  useEffect(() => {
    sendRequest(`parking/reservations-by-pass-id/${passId}`, 'get').then((res) => {
      if (res?.code === 20) {
        setTicketData(res?.data || []);
      } else if (res?.code === 21) {
        showNotification({
          title: <Text weight={500}>{t('enter.invalid.ticket.number')}</Text>,
          icon: <CircleX size={16} />,
        });
      }
    });
  }, [passId]);

  const cardPayment = () => {
    // Set WebSocket URL
    const wsurl = `ws://localhost:5000/messages`;

    // Initialize WebSocket Client
    const client = new W3CWebSocket(wsurl);

    // Handle connection error
    client.onerror = () => {
      setErrorPos(true);
    };
    if (ticketData?.result?.invoices?.invoice_status?.name !== 'paid') {
      // Handle connection success
      client.onopen = () => {
        // Connect to COM port
        function connectPort() {
          if (client.readyState === client.OPEN) {
            const comName = siteDetails?.comPortNumber;
            const sendData = JSON.stringify({
              Event: 'CONNECTION',
              Operation: 'CONNECT',
              connectionMode: 'COM',
              comName,
              braudRate: '38400',
              dataBits: '8',
              parity: 'none',
            });

            client.send(sendData);
          }
        }

        connectPort();

        // Send payment amount
        function sendAmount() {
          if (client.readyState === client.OPEN) {
            const amount = Number(ticketData?.result?.invoices?.payable_amount).toFixed(2);
            const sendAmountData = JSON.stringify({
              Event: 'TRANSACTION',
              Operation: 'PURCHASE',
              Amount: amount,
              PrintSettings: '1',
              AppId: '11',
              ECRNumber: '1234567890123456',
            });
            client.send(sendAmountData);
          }
        }

        sendAmount();
      };
    }

    // Handle connection close
    client.onclose = () => {
      // Define function to disconnect from COM port
      function closePort() {
        if (ticketData?.result?.invoices?.invoice_status?.name === 'paid') {
          const sendData = JSON.stringify({
            Event: 'CONNECTION',
            Operation: 'DISCONNECT',
          });
          client.send(sendData);
        }
      }

      // Call function to disconnect from COM port
      closePort();

      // Log message to console
      // eslint-disable-next-line no-console
      console.log('WebSocket Client Closed');
    };

    // Handle incoming messages
    client.onmessage = (message) => {
      if (typeof message.data === 'string') {
        setResponse(JSON.parse(message?.data));
      }
    };

    // Check payment status and update state
    if (ticketData?.result?.invoices?.invoice_status?.name === 'paid') {
      setCardPay(true);
    }
  };

  useEffect(() => {
    if (response?.TerminalAction === 'SWIPE_OR_INSERT') {
      setCardPay(false);
    } else if (response?.TerminalAction === 'IDLE_SCREEN') {
      setCardPay(false);
    } else if (response?.EventName === 'TERMINAL_RESPONSE') {
      setPaymentStatus(JSON.parse(response?.JsonResult));
    } else if (
      paymentStatus?.TransactionResponseEnglish ||
      paymentStatus?.TransactionResultReasonEnglish === 'Decline'
    ) {
      setPaymentStatus();
    }
  }, [
    paymentStatus?.TransactionResponseEnglish,
    paymentStatus?.TransactionResultReasonEnglish,
    response?.EventName,
    response?.JsonResult,
    response?.TerminalAction,
  ]);

  const cardNameResult = paymentStatus?.CardAcceptorName;
  const CardName = cardNameResult ? cardNameResult.split('|')[2] : null;

  useEffect(() => {
    const handlePayment = async () => {
      if (
        paymentStatus?.TransactionResponseEnglish ||
        paymentStatus?.TransactionResultReasonEnglish === 'APPROVED'
      ) {
        const body = {
          reservation_id: parseInt(ticketData?.result?.id, 10),
          transaction_id: paymentStatus?.RetrievalReferenceNumber,
          card_name: CardName,
        };
        try {
          const { code } = await sendRequestWithToken('cashier/card-payment', 'post', body);
          if (code === 20) {
            setCardPay(true);
          }
        } catch (err) {
          if (err?.response?.data?.code) {
            showNotification({
              title: <Text weight={500}>{err?.response?.data?.msg}</Text>,
              icon: <CircleX size={16} />,
            });
          }
        }
      }
    };

    handlePayment();
  }, [paymentStatus, ticketData?.result?.id]);

  useEffect(() => {
    if (passId) {
      cardPayment();
    }
  }, [passId]);

  const getResponseContent = () => {
    if (response?.TerminalAction === 'SWIPE_OR_INSERT') {
      return (
        <Title style={{ color: '#2C5864', margin: '3% 0', textAlign: 'center' }} order={3}>
          SWIPE_OR_INSERT
        </Title>
      );
    }
    if (paymentStatus?.TransactionResultReasonEnglish === 'Decline') {
      return (
        <Title style={{ color: '#2C5864', margin: '3% 0', textAlign: 'center' }} order={3}>
          Decline
        </Title>
      );
    }
    if (response?.TerminalAction === 'BUSY') {
      return (
        <Stack>
          <Center>
            <Loader
              size="md"
              variant="bars"
              color="teal"
              sx={{ width: '100px', height: '100px' }}
            />
          </Center>
          <Center>
            <Title style={{ color: '#2C5864', margin: '3% 0', textAlign: 'center' }} order={3}>
              {t('payment.your.request.is.being.processed')}
            </Title>
          </Center>
        </Stack>
      );
    }
    if (errorPos === true) {
      return (
        <Stack>
          <Title style={{ color: '#2C5864', margin: '3% 0', textAlign: 'center' }} order={3}>
            Error Connection
          </Title>
          <Center>
            <Button
              onClick={cardPayment}
              color="blue"
              radius="xl"
              size="xl"
              variant="outline"
              style={{
                width: '30%',
                paddingInline: '2% 2%',
                color: '#2C5864',
                borderColor: '#2C5864',
              }}
            >
              Retry
            </Button>
            <Space w="xl" />
            <Button
              onClick={() => navigate('/')}
              color="blue"
              radius="xl"
              size="xl"
              variant="outline"
              style={{
                width: '30%',
                paddingInline: '2% 2%',
                color: '#2C5864',
                borderColor: '#2C5864',
              }}
            >
              {appState?.language === 'ar' ? 'الرئيسية' : 'Home'}
            </Button>
          </Center>
        </Stack>
      );
    }
    return (
      <Stack>
        <Title style={{ color: '#2C5864', margin: '3% 0', textAlign: 'center' }} order={3}>
          Try Again
        </Title>
        <Center>
          <Button
            onClick={cardPayment}
            color="blue"
            radius="xl"
            size="xl"
            variant="outline"
            style={{
              width: '30%',
              paddingInline: '2% 2%',
              color: '#2C5864',
              borderColor: '#2C5864',
            }}
          >
            Retry
          </Button>
          <Space w="xl" />
          <Button
            onClick={() => navigate('/')}
            color="blue"
            radius="xl"
            size="xl"
            variant="outline"
            style={{
              width: '30%',
              paddingInline: '2% 2%',
              color: '#2C5864',
              borderColor: '#2C5864',
            }}
          >
            {appState?.language === 'ar' ? 'الرئيسية' : 'Home'}
          </Button>
        </Center>
      </Stack>
    );
  };

  return (
    <Box style={{ height: 'calc(100vh - 24px)', marginTop: '24px' }}>
      <Stack justify="space-between" style={{ height: '100%' }}>
        {paymentStatus?.TransactionResponseEnglish ||
        paymentStatus?.TransactionResultReasonEnglish === 'APPROVED' ||
        ticketData?.result?.invoices?.invoice_status?.name === 'paid' ? (
          <PaymentStatus
            invoiceDataId={ticketData?.result?.invoices?.id}
            passDataId={
              ticketData?.result?.passId === undefined ? passId : ticketData?.result?.passId
            }
          />
        ) : (
          <>
            <Box>
              <Container
                className="home"
                style={{
                  display: 'flex',
                  alignItems: 'stretch',
                  flexDirection: 'column',
                }}
              >
                <div style={{ opacity: '0.6', pointerEvents: 'none' }}>
                  {' '}
                  <PrimaryButton />
                </div>
              </Container>
            </Box>

            <Box style={{ flexGrow: '1' }}>
              <Container
                className="home no-printme"
                style={{
                  display: 'flex',
                  alignItems: 'stretch',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Box
                  className="no-printme"
                  style={{
                    height: 'fit-content',
                    marginBottom: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1',
                    justifyContent: 'space-between',
                  }}
                >
                  <Group position="apart">
                    <Stack>
                      <Title style={{ color: '#2C5864', margin: '1% 0' }} order={3}>
                        {t('payment.pay.for.ticket')} #{searchParams.get('passId')}
                      </Title>
                    </Stack>
                    <Stack>
                      <Title style={{ color: '#2C5864', margin: '1% 0' }} order={3}>
                        {t('ticket.amount.total.text')}:{' '}
                        {ticketData?.result?.invoices?.invoice_status?.name === 'paid'
                          ? 0
                          : ticketData?.result?.invoices?.payable_amount.toFixed(2)}{' '}
                        {t('ticket.amount.sar')}
                      </Title>
                    </Stack>
                  </Group>
                  <Paper shadow="sm" p="lg" radius="lg" style={{ flex: '1' }}>
                    <Group style={{ width: '100%', height: '100%' }} grow>
                      {getResponseContent()}
                    </Group>
                  </Paper>
                </Box>
              </Container>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default Home;
